/**
 * PLP/listing quick-add — Flutter listing_quick_cart_overlay.dart /
 * listing_quick_cart.dart parity (2026-08-08).
 *
 * Flutter: tapping the "+" on a single-variant, in-stock listing card adds
 * 1 unit to the (local) cart silently — no drawer, no navigation — then the
 * button becomes an inline "+ qty −" capsule; +/- taps mutate quantity
 * directly, decrementing to 0 removes the line and reverts to "+". There is
 * no debounce/loading state in Flutter because its cart write is a local
 * Isar DB call, not a network request.
 *
 * Web has to hit the real Shopify cart API (/cart/add.js, /cart/change.js),
 * so unlike Flutter this adds an in-flight guard that coalesces rapid taps
 * into one trailing request (last tap wins) instead of firing them all, and
 * a generic toast on a real failure — Flutter has nothing to port for that
 * because its local-only write can't fail the way a network call can.
 *
 * Deliberately bypasses the theme's <product-form> custom element (which
 * always dispatches cart:change with baseEvent:'variant:add', and this
 * theme's cart_type setting is site-wide "drawer" — see onCartChange_fn in
 * theme.js) so the drawer never force-opens for this surface; a distinct
 * baseEvent keeps every other cart:change listener (header count bubble,
 * cart-icon bundle, etc.) in sync without touching theme.js at all.
 */
(function () {
  if (window.erosPlpQuickAddBound) return;
  window.erosPlpQuickAddBound = true;

  var DEFAULT_CAP = 6;

  function toast(message, kind) {
    if (window.ErosUI && typeof window.ErosUI.toast === 'function') {
      window.ErosUI.toast(message, kind);
    }
  }

  // DUPLICATE: keep in sync with eros-pdp-cart-stepper.js's computeCap(). Not
  // extracted to a shared module — these two files load independently (PLP
  // vs PDP) with no guaranteed shared script between them, and consolidating
  // into eros-commerce-ui.js is deliberately avoided here to not collide
  // with the parallel money-formatter consolidation work landing there.
  function computeCap(root) {
    var tracked = root.getAttribute('data-inventory-tracked') === 'true';
    if (!tracked) return DEFAULT_CAP;
    if (root.getAttribute('data-inventory-policy') === 'continue') return DEFAULT_CAP;
    var qty = parseInt(root.getAttribute('data-inventory-quantity'), 10);
    if (isNaN(qty)) return DEFAULT_CAP;
    return Math.max(0, Math.min(qty, DEFAULT_CAP));
  }

  function findLine(cart, variantId) {
    for (var i = 0; i < cart.items.length; i++) {
      if (String(cart.items[i].variant_id) === String(variantId)) return cart.items[i];
    }
    return null;
  }

  function setState(root, qty) {
    var addBtn = root.querySelector('[data-eros-qa-add]');
    var capsule = root.querySelector('[data-eros-qa-capsule]');
    var qtyEl = root.querySelector('[data-eros-qa-qty]');
    var incBtn = root.querySelector('[data-eros-qa-increment]');
    root.dataset.qty = String(qty);
    if (qty > 0) {
      addBtn.hidden = true;
      capsule.hidden = false;
      qtyEl.textContent = String(qty);
      incBtn.disabled = qty >= computeCap(root);
    } else {
      addBtn.hidden = false;
      capsule.hidden = true;
    }
  }

  function dispatchCartChange(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'eros:listing-quick-add', cart: cart },
      })
    );
  }

  function cartRequest(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) {
          var err = new Error((json && json.description) || 'cart request failed');
          err.responseJson = json;
          throw err;
        }
        return json;
      });
    });
  }

  function fetchCart() {
    return fetch('/cart.js').then(function (r) {
      return r.json();
    });
  }

  /**
   * theme.js's CartDrawer.onCartChange_fn reads event.detail.cart.sections
   * unconditionally (cart.sections[thisDrawersSectionId]) to refresh its own
   * markup. Every other add/change flow in this theme (product-form submit,
   * line-item quantity change, quick-order-list) collects this via the same
   * cart:prepare-bundled-sections handshake and passes `sections=` on the
   * request so the response carries it — this one didn't, so cart.sections
   * was undefined and the drawer's listener threw (confirmed live via
   * console: "Cannot read properties of undefined (reading '...cart-
   * drawer')") and silently aborted before updating the drawer's item list.
   * The header count badge has its own, simpler cart:change listener that
   * doesn't touch .sections, which is why the count updated but the drawer
   * list didn't — reading as "doesn't show until you reload the cart".
   */
  function collectSectionsToBundle() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function bind(root) {
    if (root.dataset.erosQaBound === '1') return;
    root.dataset.erosQaBound = '1';

    var variantId = root.getAttribute('data-variant-id');
    var errorLabel = root.getAttribute('data-error-label') || '';
    var addBtn = root.querySelector('[data-eros-qa-add]');
    var incBtn = root.querySelector('[data-eros-qa-increment]');
    var decBtn = root.querySelector('[data-eros-qa-decrement]');

    var inFlight = false;
    var pendingQty = null;

    function currentQty() {
      return parseInt(root.dataset.qty || '0', 10);
    }

    function revertToServerState() {
      fetchCart().then(function (cart) {
        var line = findLine(cart, variantId);
        setState(root, line ? line.quantity : 0);
      });
    }

    function afterSettle() {
      inFlight = false;
      if (pendingQty !== null) {
        var next = pendingQty;
        pendingQty = null;
        runChange(next);
      }
    }

    function runChange(qty) {
      if (inFlight) {
        pendingQty = qty;
        setState(root, qty);
        return;
      }
      inFlight = true;
      setState(root, qty);
      var sections = collectSectionsToBundle();
      cartRequest('/cart/change.js', { id: variantId, quantity: qty, sections: sections.join(',') })
        .then(function (cart) {
          var line = findLine(cart, variantId);
          setState(root, line ? line.quantity : 0);
          dispatchCartChange(cart);
        })
        .catch(function () {
          revertToServerState();
          toast(errorLabel, 'error');
        })
        .finally(afterSettle);
    }

    function runAdd() {
      if (inFlight) return;
      inFlight = true;
      setState(root, 1);
      var sections = collectSectionsToBundle();
      cartRequest('/cart/add.js', { id: variantId, quantity: 1, sections: sections.join(',') })
        .then(function (addResponse) {
          // /cart/add.js's own response describes only the added item, not
          // the full cart — still need /cart.js for that — but it's the one
          // that carries the rendered `sections` HTML we requested above, so
          // graft that onto the full cart object before dispatching.
          return fetchCart().then(function (cart) {
            cart.sections = addResponse.sections;
            return cart;
          });
        })
        .then(function (cart) {
          var line = findLine(cart, variantId);
          setState(root, line ? line.quantity : 1);
          dispatchCartChange(cart);
        })
        .catch(function () {
          setState(root, 0);
          toast(errorLabel, 'error');
        })
        .finally(afterSettle);
    }

    addBtn.addEventListener('click', runAdd);
    incBtn.addEventListener('click', function () {
      var next = Math.min(currentQty() + 1, computeCap(root));
      if (next === currentQty()) return;
      runChange(next);
    });
    decBtn.addEventListener('click', function () {
      runChange(Math.max(currentQty() - 1, 0));
    });
  }

  function bindAll(scope) {
    (scope || document).querySelectorAll('[data-eros-plp-quick-add]').forEach(bind);
  }

  bindAll(document);
  document.addEventListener('shopify:section:load', function (e) {
    bindAll(e.target);
  });
  // Generic re-bind hook for content inserted well after page load via a
  // client-side fetch (e.g. eros-wishlist.js's async grid swap), which
  // this script's initial bindAll(document) call never sees.
  document.addEventListener('eros:content-loaded', function (e) {
    bindAll(e.detail && e.detail.root);
  });
})();
